import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2, Circle, Apple, Activity, Scale, Ruler, Flame, ChevronRight,
  Flame as FireIcon, Image as ImageIcon, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/* -------------------- component -------------------- */

const SHOWN_MILESTONES_KEY = "daione_milestones_seen";

function readShownMilestones() {
  try {
    return JSON.parse(localStorage.getItem(SHOWN_MILESTONES_KEY) || "[]");
  } catch {
    return [];
  }
}
function markMilestoneShown(milestone) {
  const seen = readShownMilestones();
  if (!seen.includes(milestone)) {
    seen.push(milestone);
    localStorage.setItem(SHOWN_MILESTONES_KEY, JSON.stringify(seen));
  }
}

/**
 * Daily logging checklist for the Dashboard with streak tracking.
 *
 * Behaviour:
 *   1. Self-fetches food / weight / measurement / workout-completion state.
 *   2. Animates the check icon when an item newly flips done (CSS spring pop).
 *   3. POSTs current tally to /api/checklist/record (debounced 800 ms) so the
 *      backend can compute streaks.
 *   4. Fetches /api/checklist/streak on mount + after every record POST.
 *   5. When a milestone is hit (7 / 14 / 30 / 60 / 100), shows a celebration
 *      toast with a "Share milestone" button that downloads the streak card.
 *      Each milestone is shown once per browser via localStorage.
 */
export default function DailyChecklist({ plan, calorieTarget = 2200, foodTotals = null }) {
  const [weightLatest, setWeightLatest] = useState(null);
  const [measurementLatest, setMeasurementLatest] = useState(null);
  const [completedByWeekAndDay, setCompletedByWeekAndDay] = useState({});
  const [streak, setStreak] = useState({ current_streak: 0, longest_streak: 0, qualifying_today: false, milestone: null });
  const [downloadingCard, setDownloadingCard] = useState(false);
  const prevDoneMapRef = useRef({});

  const load = async () => {
    try {
      const [w, m, s, st] = await Promise.all([
        api.get("/weight/history?days=1").catch(() => null),
        api.get("/measurements/latest").catch(() => null),
        api.get("/workouts/sessions/this-week").catch(() => null),
        api.get("/checklist/streak").catch(() => null),
      ]);
      if (w?.data?.items?.length) {
        setWeightLatest(w.data.items[w.data.items.length - 1]);
      } else {
        setWeightLatest(null);
      }
      setMeasurementLatest(m?.data || null);
      setCompletedByWeekAndDay(s?.data?.by_week_and_day || {});
      if (st?.data) setStreak(st.data);
    } catch (err) {
      console.error("DailyChecklist load failed", err);
    }
  };

  useEffect(() => {
    load();
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  /* ---- derive each item state ---- */
  const todayName = new Date().toLocaleDateString("en-US", { weekday: "long" });
  const todayIso = new Date().toISOString().slice(0, 10);

  const todayWorkoutDay = plan?.days?.find((d) =>
    d.day?.toLowerCase().includes(todayName.toLowerCase())
  );
  const isTrainingDay = !!(todayWorkoutDay && (todayWorkoutDay.exercises?.length || 0) > 0);
  const trainingDayLabel = todayWorkoutDay?.day || todayName;
  const workoutComplete = (() => {
    if (!isTrainingDay) return true;
    for (const wk of Object.values(completedByWeekAndDay || {})) {
      if (wk && wk[trainingDayLabel]) return true;
    }
    return false;
  })();

  const mealsLogged = (foodTotals?.calories || 0) > 0;
  const calorieTargetHit = (foodTotals?.calories || 0) >= calorieTarget * 0.8;
  const weightLogged = weightLatest?.logged_on === todayIso;
  const measurementLogged = (() => {
    const lo = measurementLatest?.logged_on;
    if (!lo) return false;
    const now = new Date();
    const start = new Date(now);
    start.setDate(now.getDate() - now.getDay());
    const startIso = start.toISOString().slice(0, 10);
    return lo >= startIso && lo <= todayIso;
  })();

  const items = [
    {
      key: "workout",
      icon: Activity,
      label: isTrainingDay
        ? `Complete today's workout · ${todayWorkoutDay?.focus || ""}`.trim()
        : "Rest day · take it easy",
      done: workoutComplete,
      to: "/app/workout",
      ctaLabel: "Open workout",
      hideCta: !isTrainingDay,
    },
    {
      key: "meal",
      icon: Apple,
      label: "Log a meal",
      done: mealsLogged,
      to: "/app/nutrition",
      ctaLabel: "Log food",
    },
    {
      key: "calories",
      icon: Flame,
      label: `Hit ~80% of your ${calorieTarget} kcal target`,
      done: calorieTargetHit,
      to: "/app/nutrition",
      ctaLabel: "Log food",
      hideCta: calorieTargetHit,
    },
    {
      key: "weight",
      icon: Scale,
      label: "Log today's body weight",
      done: weightLogged,
      to: "/app/profile",
      ctaLabel: "Log weight",
    },
    {
      key: "measurement",
      icon: Ruler,
      label: "Update body measurements this week",
      done: measurementLogged,
      to: "/app/measurements",
      ctaLabel: "Log tape",
    },
  ];

  const total = items.length;
  const done = items.filter((i) => i.done).length;
  const pct = Math.round((done / total) * 100);

  /* ---- detect which items just newly flipped (drives the CSS pop) ---- */
  const justFlipped = {};
  for (const it of items) {
    const wasDone = prevDoneMapRef.current[it.key];
    justFlipped[it.key] = it.done && wasDone === false;
  }
  // Update ref AFTER computing so we capture the transition cleanly.
  useEffect(() => {
    const next = {};
    for (const it of items) next[it.key] = it.done;
    prevDoneMapRef.current = next;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.map((i) => `${i.key}:${i.done}`).join("|")]);

  /* ---- POST tally to backend (debounced) ---- */
  const recordTimerRef = useRef(null);
  useEffect(() => {
    if (recordTimerRef.current) clearTimeout(recordTimerRef.current);
    recordTimerRef.current = setTimeout(async () => {
      try {
        const { data } = await api.post("/checklist/record", { items_done: done, items_total: total });
        if (data) {
          setStreak(data);
          if (data.newly_unlocked_badge) {
            const b = data.newly_unlocked_badge;
            toast.success(`Badge unlocked: ${b.label}!`, {
              description: "Tap Share to celebrate.",
              duration: 8000,
              action: {
                label: "Share",
                onClick: () => downloadStreakCard(b.tier, data.longest_streak || b.tier),
              },
            });
          }
        }
      } catch (err) {
        console.error("checklist record failed", err);
      }
    }, 800);
    return () => recordTimerRef.current && clearTimeout(recordTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done, total]);

  /* ---- milestone celebration ---- */
  useEffect(() => {
    const m = streak.milestone;
    if (!m) return;
    const seen = readShownMilestones();
    const key = `${todayIso}:${m}`; // namespace per-day so the same milestone can re-show after a streak break + re-hit
    if (seen.includes(key)) return;
    markMilestoneShown(key);
    toast.success(`${m}-day streak unlocked!`, {
      description: "Tap to share your milestone.",
      duration: 7000,
      action: {
        label: "Share",
        onClick: () => downloadStreakCard(m, streak.longest_streak),
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streak.milestone, streak.longest_streak]);

  const downloadStreakCard = async (days = streak.current_streak, longest = streak.longest_streak) => {
    setDownloadingCard(true);
    try {
      const { data } = await api.post(
        "/share/card",
        { type: "streak", payload: { streak_days: days, longest } },
        { responseType: "blob" }
      );
      const objUrl = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = `daione-streak-${days}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(objUrl), 1000);
      toast.success("Streak card downloaded");
    } catch {
      toast.error("Could not generate card");
    } finally {
      setDownloadingCard(false);
    }
  };

  return (
    <div
      className="border border-white/10 bg-[#121212] p-6 mb-6"
      data-testid="daily-checklist"
    >
      <div className="flex items-center justify-between mb-2 gap-4">
        <div className="min-w-0">
          <div className="label-eyebrow text-[#6B9BFF] mb-1">Today&apos;s checklist</div>
          <div className="font-display text-2xl leading-none">
            {done} of {total} logged
          </div>
        </div>

        {streak.current_streak > 0 ? (
          <button
            type="button"
            onClick={() => downloadStreakCard()}
            disabled={downloadingCard}
            className="group inline-flex items-center gap-2 px-3 py-2 border border-[#FF6A3D]/30 bg-[#FF6A3D]/10 hover:border-[#FF6A3D] transition-colors disabled:opacity-50"
            data-testid="streak-badge"
            title="Share your streak"
          >
            {downloadingCard ? (
              <Loader2 className="w-3.5 h-3.5 text-[#FF6A3D] animate-spin" />
            ) : (
              <FireIcon className="w-3.5 h-3.5 text-[#FF6A3D]" />
            )}
            <div className="text-left leading-tight">
              <div className="font-display text-lg text-white" data-testid="streak-count">
                {streak.current_streak}
              </div>
              <div className="label-eyebrow text-[#FF6A3D] text-[9px]">day streak</div>
            </div>
            <ImageIcon className="w-3 h-3 text-white/30 group-hover:text-white/60 ml-1 hidden sm:block" />
          </button>
        ) : (
          <div className="text-right">
            <div className="font-display text-3xl text-[#6B9BFF]" data-testid="checklist-pct">
              {pct}%
            </div>
            <div className="label-eyebrow text-white/40 text-[10px]">complete</div>
          </div>
        )}
      </div>

      <div className="h-1.5 bg-white/10 mb-5">
        <div
          className="h-full bg-[#6B9BFF] transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>

      <ul className="space-y-2">
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <li
              key={it.key}
              data-testid={`checklist-item-${it.key}`}
              data-done={it.done ? "true" : "false"}
              className={`flex items-center gap-3 px-3 py-2.5 border transition-colors ${
                it.done
                  ? "border-[#6B9BFF]/30 bg-[#6B9BFF]/5"
                  : "border-white/10 hover:border-white/25"
              }`}
            >
              {it.done ? (
                <CheckCircle2
                  className={`w-4 h-4 text-[#6B9BFF] shrink-0 ${
                    justFlipped[it.key] ? "checklist-pop" : ""
                  }`}
                />
              ) : (
                <Circle className="w-4 h-4 text-white/30 shrink-0" />
              )}
              <Icon className={`w-4 h-4 shrink-0 ${it.done ? "text-[#6B9BFF]" : "text-white/40"}`} />
              <span
                className={`flex-1 text-sm ${
                  it.done ? "text-white/60 line-through decoration-white/20" : "text-white"
                }`}
              >
                {it.label}
              </span>
              {!it.done && !it.hideCta && (
                <Link
                  to={it.to}
                  className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest font-bold text-[#6B9BFF] hover:text-white"
                  data-testid={`checklist-cta-${it.key}`}
                >
                  {it.ctaLabel} <ChevronRight className="w-3 h-3" />
                </Link>
              )}
            </li>
          );
        })}
      </ul>

      {streak.current_streak > 0 && (
        <div className="mt-4 text-[10px] text-white/40 text-center label-eyebrow" data-testid="streak-hint">
          {streak.longest_streak > streak.current_streak
            ? `Personal best: ${streak.longest_streak} days · Keep going!`
            : "Beat your best — keep the streak alive!"}
        </div>
      )}
    </div>
  );
}
